import { IconCoins, IconGift } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import { DAILY_CHECK_IN_ENABLED } from '@/config/credit-economics';
import { cn } from '@/lib/utils';
import { m } from '@/locale/paraglide/messages';

export interface VideoCreditTrialActions {
  onCheckIn: () => void;
  onUseTrialPreset: () => void;
  presetModelLabel?: string;
}

interface VideoCreditTrialGuidanceProps extends VideoCreditTrialActions {
  cost: number;
  presentation: 'login' | 'pricing';
}

export function VideoCreditTrialGuidance({
  cost,
  onCheckIn,
  onUseTrialPreset,
  presetModelLabel,
  presentation,
}: VideoCreditTrialGuidanceProps) {
  const isLogin = presentation === 'login';

  return (
    <div
      data-testid="video-credit-trial-guidance"
      className={cn(
        isLogin
          ? 'mt-3 border-amber-400/20 border-t pt-3'
          : 'mb-5 rounded-lg border border-amber-300/20 bg-amber-300/5 p-4'
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'flex shrink-0 items-center justify-center rounded-lg',
            isLogin
              ? 'size-8 bg-amber-400/12 text-amber-600 dark:text-amber-300'
              : 'size-9 bg-amber-300/12 text-amber-200'
          )}
        >
          <IconCoins className="size-4" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p
            className={cn(
              'font-semibold leading-tight',
              isLogin ? 'text-sm text-foreground' : 'text-sm text-amber-100'
            )}
          >
            {m.video_credit_trial_title({ cost: cost.toLocaleString() })}
          </p>
          <p
            className={cn(
              'mt-1 text-xs leading-5',
              isLogin ? 'text-muted-foreground' : 'text-white/62'
            )}
          >
            {m.video_credit_trial_description()}
          </p>
        </div>
      </div>
      <div className="mt-3 flex flex-col items-center justify-center gap-2 sm:flex-row">
        <Button
          type="button"
          size="sm"
          variant={isLogin ? 'outline' : 'secondary'}
          className={cn(!isLogin && 'bg-white text-black hover:bg-white/90')}
          onClick={onUseTrialPreset}
        >
          {presetModelLabel
            ? m.video_credit_trial_apply_model({ model: presetModelLabel })
            : m.video_credit_trial_apply()}
        </Button>
        {DAILY_CHECK_IN_ENABLED ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={cn(
              isLogin
                ? 'text-amber-700 hover:bg-amber-400/10 hover:text-amber-800 dark:text-amber-300 dark:hover:text-amber-200'
                : 'text-amber-200 hover:bg-amber-300/10 hover:text-amber-100'
            )}
            onClick={onCheckIn}
          >
            <IconGift className="size-3.5" aria-hidden="true" />
            {m.video_credit_trial_check_in()}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
